import {
  CollabDeliveryChannel,
  ErrorCode,
  hasUnresolvedDeliveryFailure,
  type CollabResponse,
  type CollabDeliveryStatus,
} from '@fluenceur/types';
import { ChatBubble, Link as LinkIcon, MailOut, WarningTriangle } from 'iconoir-react';
import type { ComponentType, SVGProps } from 'react';

/**
 * Delivery display taxonomy for FOU-836, shared by the wizard done-step (F2 —
 * per-line delivery badge) and the tracking detail (F3 — failure banner). Keeps
 * the error-code → cause copy and the per-status badge in one place so the two
 * surfaces never drift.
 */

/**
 * i18n key for the short failure cause of a delivery error. Known email codes
 * get dedicated copy; anything else (or a missing code) falls back to a generic
 * line. The code doubles as the leaf key, so the same map serves any code the
 * backend surfaces.
 */
export const deliveryFailureCauseKey = (code: string | null | undefined): string => {
  switch (code) {
    case ErrorCode.E_EMAIL_SEND_OUTCOME_UNKNOWN:
    case ErrorCode.E_EMAIL_MESSAGE_TOO_LARGE:
    case ErrorCode.E_ATTACHMENT_EMPTY:
    case ErrorCode.E_ATTACHMENT_INVALID_TYPE:
    case ErrorCode.E_ATTACHMENT_TOO_LARGE:
    case ErrorCode.E_ATTACHMENT_BATCH_SIZE_EXCEEDED:
    case ErrorCode.E_ATTACHMENT_MAX_COUNT:
    case ErrorCode.E_ATTACHMENT_NOT_FOUND:
    case ErrorCode.E_ATTACHMENT_MAGIC_BYTE_MISMATCH:
    case ErrorCode.E_ATTACHMENT_PROVIDER_SIZE_EXCEEDED:
      return `errors.${code}`;
    case ErrorCode.E_EMAIL_SUPPRESSED:
    case ErrorCode.E_EMAIL_INTEGRATION_DISCONNECTED:
    case ErrorCode.E_EMAIL_QUOTA_EXCEEDED:
    case ErrorCode.E_EMAIL_RECIPIENT_INVALID:
    case ErrorCode.E_EMAIL_API_ERROR:
      return `collabs.tracking.deliveryFailure.causes.${code}`;
    default:
      return 'collabs.tracking.deliveryFailure.causes.generic';
  }
};

/**
 * Delivery outcomes where the proposal actually REACHED the recipient — so the
 * personal link is only a secondary "or copy" affordance on the done-step, not
 * the primary delivery action. `link_only` and `failed` are excluded: there the
 * link IS the delivery (or the fallback), so it stays primary. Type predicate
 * so callers narrow the status to the delivered subset.
 */
export type DeliveredStatus = 'sent_email' | 'notified_affiliate' | 'sent_dm';

export const isDeliveredStatus = (
  status: CollabDeliveryStatus | undefined
): status is DeliveredStatus =>
  status === 'sent_email' || status === 'notified_affiliate' || status === 'sent_dm';

/** Tone drives the badge colour; kept abstract so callers own the exact tokens. */
export type DeliveryStatusTone = 'ok' | 'bad' | 'neutral';

export interface DeliveryStatusMeta {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  tone: DeliveryStatusTone;
  /** i18n key for the badge label. */
  labelKey: string;
}

/**
 * Per-line delivery outcome badge (F2 done-step). `sent_dm` reads as "link in
 * conversation" and `link_only` as "link to share" — both are successful
 * outcomes, only `failed` is a real problem (its cause comes from
 * {@link deliveryFailureCauseKey}).
 */
export const DELIVERY_STATUS_META: Record<CollabDeliveryStatus, DeliveryStatusMeta> = {
  sent_email: {
    icon: MailOut,
    tone: 'ok',
    labelKey: 'collabs.tracking.deliveryStatus.sentEmail',
  },
  notified_affiliate: {
    icon: MailOut,
    tone: 'ok',
    labelKey: 'collabs.tracking.deliveryStatus.notifiedAffiliate',
  },
  sent_dm: {
    icon: ChatBubble,
    tone: 'ok',
    labelKey: 'collabs.tracking.deliveryStatus.sentDm',
  },
  link_only: {
    icon: LinkIcon,
    tone: 'neutral',
    labelKey: 'collabs.tracking.deliveryStatus.linkOnly',
  },
  failed: {
    icon: WarningTriangle,
    tone: 'bad',
    labelKey: 'collabs.tracking.deliveryStatus.failed',
  },
};

/** Actual owner facts, independent of the channel the UI requested. */
export type CollabOwnerDeliveryReceipt = Pick<
  CollabResponse,
  'deliveryChannel' | 'deliveredAt' | 'deliveryFailedAt' | 'deliveryErrorCode'
>;
export type CollabOwnerDeliveryState =
  | CollabDeliveryStatus
  | 'pending'
  | 'unconfirmed'
  | 'confirmed';

export const deriveOwnerDeliveryState = (
  receipt: CollabOwnerDeliveryReceipt
): CollabOwnerDeliveryState => {
  if (hasUnresolvedDeliveryFailure(receipt)) return 'failed';
  if (receipt.deliveryChannel === CollabDeliveryChannel.LINK_ONLY) return 'link_only';
  const confirmed = !!receipt.deliveredAt && Number.isFinite(Date.parse(receipt.deliveredAt));
  if (confirmed) {
    switch (receipt.deliveryChannel) {
      case CollabDeliveryChannel.EMAIL_CONNECTED:
        return 'sent_email';
      case CollabDeliveryChannel.EMAIL_TRANSACTIONAL:
        return 'notified_affiliate';
      case CollabDeliveryChannel.DM:
        return 'sent_dm';
      default:
        return 'confirmed';
    }
  }
  return receipt.deliveryChannel ? 'pending' : 'unconfirmed';
};
